import type { ReactNode } from "react";

interface Props {
  id: string;
  label: string;
  count: number;
  accent: "critical" | "muted";
  /** one-line statement of what this tier is FOR — a CM shouldn't have to infer it */
  blurb: string;
  children: ReactNode;
}

export default function TierSection({
  id,
  label,
  count,
  accent,
  blurb,
  children,
}: Props) {
  // clinic/crit tokens (tailwind.config.js) — Horizon has no `slate`, its `gray`
  // is blue-tinted and non-monotonic, and `red-50` is a 10%-alpha wash.
  const dot = accent === "critical" ? "bg-crit-500" : "bg-clinic-400";
  const text =
    accent === "critical"
      ? "text-crit-600 dark:text-red-400"
      : "text-clinic-600 dark:text-gray-400";

  /**
   * ALARM state: a critical tier that actually HAS work is the thing a CM must see
   * first — the count badge goes solid red, the dot pulses, the panel border warms,
   * and an ACT NOW tag sits in the header. A critical tier at zero stays calm
   * (if the quiet state also shouted, the shout would mean nothing), and the muted
   * tier can never alarm — P2 is proactive work by definition.
   */
  const alarm = accent === "critical" && count > 0;

  return (
    <section className="mb-7" aria-labelledby={id}>
      <div className="mb-1 flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={`h-2 w-2 rounded-sm ${dot} ${
            alarm ? "animate-pulse motion-reduce:animate-none" : ""
          }`}
        />
        <h2
          id={id}
          className={`font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${text}`}
        >
          {label}
        </h2>
        <span
          data-testid="tier-count"
          className={`rounded px-1.5 py-px font-mono text-[11px] font-semibold tabular-nums ${
            alarm
              ? "bg-crit-600 text-white"
              : "bg-clinic-100 text-clinic-600 dark:bg-navy-700 dark:text-gray-300"
          }`}
        >
          {count}
        </span>
        {alarm && (
          <span
            data-testid="tier-alarm"
            className="rounded bg-crit-600 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-white"
          >
            Act now
          </span>
        )}
        <hr className="flex-1 border-clinic-200 dark:border-navy-700" />
      </div>
      <p className="mb-2.5 ml-[18px] text-xs text-clinic-500 dark:text-gray-400">
        {blurb}
      </p>
      {/*
        One bordered panel per tier, rows separated by hairlines — a queue reads as a
        single instrument, not a scatter of floating cards. Rows carry their own
        severity rail + wash on top of this.
      */}
      {/* no overflow-hidden — the snooze menu must escape the panel on the last row;
          rows round their own end corners instead (first:/last: in WorklistCard) */}
      <ul
        className={`flex list-none flex-col divide-y divide-clinic-100 rounded-lg border bg-white shadow-sm dark:divide-navy-700 dark:!bg-navy-800 ${
          alarm
            ? "border-crit-200 dark:border-crit-700"
            : "border-clinic-200 dark:border-navy-700"
        }`}
      >
        {children}
      </ul>
    </section>
  );
}
